/** Reading the served edge matrix.
 *
 * The matrix itself lives in exactly one place — `server/src/graphmind/graphs/
 * ontology.py` — and reaches the studio over `GET /api/graph/ontology`. This
 * module is the small amount of logic needed to USE it: match a (source,
 * target) pair against the allowed patterns, where "*" means any node type.
 *
 * Nothing here encodes which pairs are legal. If this file and the server ever
 * disagree, the server wins, because the write chokepoint re-validates every
 * edge before it is stored.
 */
import type { EdgeTypeRule, NodeType } from "@/lib/api";

const ANY = "*";

function matches(rule: EdgeTypeRule, src: NodeType, dst: NodeType): boolean {
  return rule.pairs.some(
    ([s, d]) => (s === ANY || s === src) && (d === ANY || d === dst),
  );
}

/** The relationships a person may draw from `src` to `dst`, in the order the
 *  server listed them. Empty until the matrix has loaded, which is the honest
 *  answer — offering a guess would mean offering an edge the server may
 *  reject. */
export function authorableTypes(
  rules: EdgeTypeRule[] | undefined,
  src: NodeType,
  dst: NodeType,
): EdgeTypeRule[] {
  if (!rules) return [];
  return rules.filter((r) => r.authorable && matches(r, src, dst));
}

/** "is similar to" for `similar_to`. Falls back to the underscored type so a
 *  newly added edge type still reads as words rather than as nothing. */
export function edgePhrase(rules: EdgeTypeRule[] | undefined, type: string): string {
  return rules?.find((r) => r.type === type)?.phrase ?? type.replace(/_/g, " ");
}
